import type * as React from 'react';

import { twMerge } from 'tailwind-merge';

const Table = ({
  ref,
  className,
  containerClassName,
  containerRef,
  allowScroll = true,
  containOverscroll = false,
  ...props
}: React.ComponentProps<'table'> & {
  containerClassName?: string;
  containerRef?: React.Ref<HTMLDivElement>;
  allowScroll?: boolean;
  containOverscroll?: boolean;
}) => (
  <div
    ref={containerRef}
    className={twMerge(
      'w-full',
      allowScroll && 'overflow-auto',
      allowScroll && containOverscroll && 'overscroll-contain',
      containerClassName
    )}
  >
    <table ref={ref} className={twMerge('w-full caption-bottom text-sm', className)} {...props} />
  </div>
);

const TableHeader = ({
  ref,
  className,
  flatRoundedCorners = false,
  ...props
}: React.ComponentProps<'thead'> & { flatRoundedCorners?: boolean }) => (
  <thead
    ref={ref}
    className={twMerge(
      '[&_tr]:border-b',
      !flatRoundedCorners && '[&_tr_th:first-child]:rounded-tl-xl [&_tr_th:last-child]:rounded-tr-xl',
      className
    )}
    {...props}
  />
);

const TableBody = ({ ref, className, ...props }: React.ComponentProps<'tbody'>) => (
  <tbody ref={ref} className={twMerge('[&_tr:last-child]:border-0', className)} {...props} />
);

const TableFooter = ({ ...props }: React.ComponentProps<'tfoot'>) => <tfoot {...props} />;

const TableRow = ({
  ref,
  className,
  allowSelectionStyle = true,
  ...props
}: React.ComponentProps<'tr'> & { allowSelectionStyle?: boolean }) => (
  <tr
    ref={ref}
    className={twMerge(
      'border-c-border-muted border-b transition-colors',
      allowSelectionStyle && 'data-[state=selected]:bg-c-background!',
      allowSelectionStyle && 'focus-visible:border-primary focus-visible:bg-c-background! focus-visible:outline-none',
      className
    )}
    {...props}
  />
);

const TableHead = ({ ref, className, ...props }: React.ComponentProps<'th'>) => (
  <th
    ref={ref}
    className={twMerge(
      'text-c-card-foreground h-14 pr-1 pl-1 text-left align-middle leading-tight font-bold first:pl-2 last:pr-2',
      className
    )}
    {...props}
  />
);

const TableCell = ({ ref, className, ...props }: React.ComponentProps<'td'>) => (
  <td ref={ref} className={twMerge('t-cell px-2 py-2 align-middle', className)} {...props} />
);

const TableCaption = ({ ref, className, ...props }: React.ComponentProps<'caption'>) => (
  <caption ref={ref} className={twMerge('text-muted-foreground mt-4 text-sm', className)} {...props} />
);

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
